import React, { useState, useEffect, useRef } from 'react';
import AdminLayout from '@/components/AdminLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Settings, Upload, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [settings, setSettings] = useState({
    logo_url: '',
    phone: '',
    whatsapp: '',
    email: '',
    address: '',
    instagram: '',
    facebook: '',
    schedule: '',
  });

  const getToken = () => localStorage.getItem('token');
  const CHUNK_SIZE = 4 * 1024 * 1024;

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/settings`);
      setSettings(prev => ({ ...prev, ...res.data }));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/settings`, settings, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success('Setări salvate cu succes!');
    } catch (e) { toast.error('Eroare la salvare'); }
    finally { setSaving(false); }
  };

  const uploadChunked = async (file) => {
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const initRes = await axios.post(`${API_URL}/api/upload/init`, {
      filename: file.name, total_chunks: totalChunks
    }, { headers: { Authorization: `Bearer ${getToken()}` } });
    for (let i = 0; i < totalChunks; i++) {
      const chunk = file.slice(i * CHUNK_SIZE, Math.min((i + 1) * CHUNK_SIZE, file.size));
      const fd = new FormData(); fd.append('file', chunk, `chunk_${i}`);
      await axios.post(`${API_URL}/api/upload/chunk/${initRes.data.upload_id}/${i}`, fd, {
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'multipart/form-data' }, timeout: 60000
      });
    }
    return (await axios.post(`${API_URL}/api/upload/complete/${initRes.data.upload_id}`, {
      filename: file.name, total_chunks: totalChunks, content_type: file.type
    }, { headers: { Authorization: `Bearer ${getToken()}` } })).data;
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      let result;
      if (file.size > CHUNK_SIZE) { result = await uploadChunked(file); }
      else {
        const fd = new FormData(); fd.append('file', file);
        result = (await axios.post(`${API_URL}/api/upload`, fd, {
          headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'multipart/form-data' }
        })).data;
      }
      setSettings(prev => ({ ...prev, logo_url: result.url }));
      toast.success('Logo încărcat!');
    } catch (e) { toast.error('Eroare la încărcare'); }
    finally { setUploading(false); }
  };

  const s = (field, val) => setSettings(prev => ({ ...prev, [field]: val }));
  const logoSrc = settings.logo_url?.startsWith('/api') ? `${API_URL}${settings.logo_url}` : settings.logo_url;

  if (loading) return (
    <AdminLayout title="Setări Site">
      <div className="text-center py-12"><div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto" /></div>
    </AdminLayout>
  );

  return (
    <AdminLayout title="Setări Site">
      <div data-testid="admin-settings" className="max-w-3xl">
        <div className="bg-[#121212] border border-[#D4AF37]/20 p-4 mb-6 text-sm">
          <p className="text-[#D4AF37] font-semibold mb-1">Setări Generale</p>
          <p className="text-[#A3A3A3]">Modificați datele de contact, logo-ul și linkurile social media. Se propagă automat în tot site-ul.</p>
        </div>

        <div className="admin-card p-6">
          <div className="flex items-center gap-3 mb-6">
            <Settings className="text-[#D4AF37]" size={24} />
            <h2 className="text-white font-serif text-xl">Setări Generale</h2>
          </div>

          {/* Logo */}
          <div className="mb-8 pb-6 border-b border-white/10">
            <label className="block text-[#D4AF37] text-sm font-semibold uppercase tracking-wider mb-4">Logo</label>
            <div className="flex items-start gap-4">
              {logoSrc && (
                <div className="relative w-40 h-20 bg-[#1a1a1a] border border-white/10 overflow-hidden flex-shrink-0">
                  <img src={logoSrc} alt="Logo" className="w-full h-full object-contain" />
                  <button onClick={() => s('logo_url', '')} className="absolute top-1 right-1 bg-black/70 text-white p-0.5 rounded-full hover:bg-red-600">
                    <X size={12} />
                  </button>
                </div>
              )}
              <div className="flex-1">
                <input type="text" value={settings.logo_url || ''} onChange={e => s('logo_url', e.target.value)} placeholder="URL logo sau încarcă..." className="form-input w-full text-sm mb-2" />
                <input ref={fileRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                <button onClick={() => fileRef.current?.click()} disabled={uploading} className="text-xs bg-[#1a1a1a] border border-white/10 text-[#A3A3A3] hover:text-white px-3 py-1.5 flex items-center gap-1">
                  <Upload size={12} /> {uploading ? 'Se încarcă...' : 'Încarcă logo'}
                </button>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="mb-8 pb-6 border-b border-white/10">
            <label className="block text-[#D4AF37] text-sm font-semibold uppercase tracking-wider mb-4">Date Contact</label>
            <div className="space-y-4">
              <div>
                <label className="block text-[#A3A3A3] text-sm mb-1">Telefon</label>
                <input type="text" value={settings.phone || ''} onChange={e => s('phone', e.target.value)} className="form-input w-full" placeholder="0761 961 789" />
              </div>
              <div>
                <label className="block text-[#A3A3A3] text-sm mb-1">WhatsApp (nr. internațional)</label>
                <input type="text" value={settings.whatsapp || ''} onChange={e => s('whatsapp', e.target.value)} className="form-input w-full" placeholder="40761961789" />
              </div>
              <div>
                <label className="block text-[#A3A3A3] text-sm mb-1">Email</label>
                <input type="text" value={settings.email || ''} onChange={e => s('email', e.target.value)} className="form-input w-full" placeholder="vanzari@shagunsurfaces.ro" />
              </div>
              <div>
                <label className="block text-[#A3A3A3] text-sm mb-1">Adresă</label>
                <input type="text" value={settings.address || ''} onChange={e => s('address', e.target.value)} className="form-input w-full" placeholder="Str. Henri Coandă 33A, Brașov" />
              </div>
              <div>
                <label className="block text-[#A3A3A3] text-sm mb-1">Program</label>
                <input type="text" value={settings.schedule || ''} onChange={e => s('schedule', e.target.value)} className="form-input w-full" placeholder="Luni - Vineri: 09:00 - 18:00" />
              </div>
            </div>
          </div>

          {/* Social */}
          <div className="mb-6">
            <label className="block text-[#D4AF37] text-sm font-semibold uppercase tracking-wider mb-4">Social Media</label>
            <div className="space-y-4">
              <div>
                <label className="block text-[#A3A3A3] text-sm mb-1">Instagram</label>
                <input type="text" value={settings.instagram || ''} onChange={e => s('instagram', e.target.value)} className="form-input w-full" placeholder="https://www.instagram.com/shagunsurfaces/" />
              </div>
              <div>
                <label className="block text-[#A3A3A3] text-sm mb-1">Facebook</label>
                <input type="text" value={settings.facebook || ''} onChange={e => s('facebook', e.target.value)} className="form-input w-full" placeholder="https://www.facebook.com/..." />
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-4 border-t border-white/10">
            <button onClick={handleSave} disabled={saving} className="btn-primary flex items-center gap-2" data-testid="save-settings-btn">
              <Save size={18} /> {saving ? 'Se salvează...' : 'Salvează Setările'}
            </button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
